import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  Chip,
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Avatar,
  Tab,
  Tabs,
} from "@mui/material";
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Check,
  Close,
  PictureAsPdf,
  Sms,
  AttachMoney,
  EventNote,
  CheckCircle,
  Cancel,
  Person,
  Phone,
  Email,
  Home,
  CalendarMonth,
  ChildCare,
  MedicalServices,
  Notes,
  Wc,
  Cake,
  Psychology,
  Accessible,
} from "@mui/icons-material";
import {
  fetchSessions,
  createSession,
  updateSession,
  updateSessionPayment,
  deleteSession,
} from "../redux/sessionSlice";
import { fetchCustomers } from "../redux/customerSlice";
import {
  fetchChildrenByCustomer,
  createChild,
  updateChild,
  deleteChild,
} from "../redux/childSlice";
import { fetchPackages } from "../redux/packageSlice";
import {
  fetchCustomerPackages,
  createCustomerPackage,
  updateCustomerPackagePayment,
  deleteCustomerPackage,
} from "../redux/customerPackageSlice";

const AdminCustomerDetailPage = () => {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { items: sessions } = useSelector((state) => state.sessions);
  const { items: customers } = useSelector((state) => state.customers);
  const { items: children } = useSelector((state) => state.children);
  const { items: packages } = useSelector((state) => state.packages);
  const { items: customerPackages } = useSelector((state) => state.customerPackages);

  const [customer, setCustomer] = useState(null);
  const [customerSessions, setCustomerSessions] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openSmsDialog, setOpenSmsDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [smsMessage, setSmsMessage] = useState("");
  const [currentTab, setCurrentTab] = useState(0);
  const [openChildDialog, setOpenChildDialog] = useState(false);
  const [editingChild, setEditingChild] = useState(null);
  const [confirmDeleteChild, setConfirmDeleteChild] = useState(false);
  const [childToDelete, setChildToDelete] = useState(null);

  // Paket state'leri
  const [openPackageDialog, setOpenPackageDialog] = useState(false);
  const [packageFormData, setPackageFormData] = useState({
    packageId: "",
    purchaseDate: new Date().toISOString().split("T")[0],
    expiryDate: "",
    isPaid: false,
    paymentDate: "",
    paymentMethod: "",
    notes: "",
  });
  const [confirmDeletePackage, setConfirmDeletePackage] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);

  const [childFormData, setChildFormData] = useState({
    customerId: "",
    name: "",
    birthDate: "",
    gender: "",
    diagnosis: "",
    medicalNotes: "",
    specialNeeds: "",
    notes: "",
  });

  const [formData, setFormData] = useState({
    customerId: "",
    childId: "",
    sessionDate: "",
    sessionType: "",
    duration: 60,
    price: "",
    isPaid: false,
    paymentDate: "",
    paymentMethod: "",
    notes: "",
    status: "scheduled",
  });

  const [dateFilter, setDateFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState(null);

  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  useEffect(() => {
    dispatch(fetchCustomers({}));
    dispatch(fetchSessions({}));
    dispatch(fetchPackages({ isActive: "true" }));
  }, [dispatch]);

  useEffect(() => {
    if (customerId) {
      dispatch(fetchChildrenByCustomer(customerId));
      dispatch(fetchCustomerPackages(customerId));
    }
  }, [customerId, dispatch]);

  useEffect(() => {
    const foundCustomer = customers.find((c) => c._id === customerId);
    setCustomer(foundCustomer);
  }, [customers, customerId]);

  useEffect(() => {
    if (customerId && sessions.length > 0) {
      const filtered = sessions.filter((s) => s.customerId?._id === customerId);
      setCustomerSessions(filtered);
    }
  }, [sessions, customerId]);

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
  };

  const getFilteredSessions = () => {
    let filtered = [...customerSessions];

    if (dateFilter.startDate) {
      filtered = filtered.filter(
        (s) => new Date(s.sessionDate) >= new Date(dateFilter.startDate)
      );
    }

    if (dateFilter.endDate) {
      filtered = filtered.filter(
        (s) => new Date(s.sessionDate) <= new Date(dateFilter.endDate)
      );
    }

    return filtered;
  };

  const calculateStats = () => {
    const filteredSessions = getFilteredSessions();
    const totalSessions = filteredSessions.length;
    const paidSessions = filteredSessions.filter((s) => s.isPaid).length;
    const unpaidSessions = filteredSessions.filter((s) => !s.isPaid).length;
    const totalPaid = filteredSessions
      .filter((s) => s.isPaid)
      .reduce((sum, s) => sum + s.price, 0);
    const totalUnpaid = filteredSessions
      .filter((s) => !s.isPaid)
      .reduce((sum, s) => sum + s.price, 0);
    const totalRevenue = totalPaid + totalUnpaid;

    return {
      totalSessions,
      paidSessions,
      unpaidSessions,
      totalPaid,
      totalUnpaid,
      totalRevenue,
    };
  };

  const handleOpenDialog = (session = null) => {
    if (session) {
      setEditingSession(session);
      setFormData({
        customerId: session.customerId._id,
        childId: session.childId._id,
        sessionDate: new Date(session.sessionDate).toISOString().split("T")[0],
        sessionType: session.sessionType,
        duration: session.duration,
        price: session.price,
        isPaid: session.isPaid,
        paymentDate: session.paymentDate
          ? new Date(session.paymentDate).toISOString().split("T")[0]
          : "",
        paymentMethod: session.paymentMethod || "",
        notes: session.notes || "",
        status: session.status,
      });
    } else {
      setEditingSession(null);
      setFormData({
        customerId: customerId,
        childId: "",
        sessionDate: "",
        sessionType: "",
        duration: 60,
        price: "",
        isPaid: false,
        paymentDate: "",
        paymentMethod: "",
        notes: "",
        status: "scheduled",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingSession(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingSession) {
        await dispatch(
          updateSession({ id: editingSession._id, data: formData })
        ).unwrap();
        showToast("Seans güncellendi");
      } else {
        await dispatch(createSession(formData)).unwrap();
        showToast("Seans oluşturuldu");
      }
      handleCloseDialog();
      dispatch(fetchSessions({}));
    } catch (error) {
      showToast(error || "İşlem başarısız", "error");
    }
  };

  // Çocuk Dialog İşlemleri
  const handleOpenChildDialog = (child = null) => {
    if (child) {
      setEditingChild(child);
      setChildFormData({
        customerId: child.customerId,
        name: child.name,
        birthDate: child.birthDate
          ? new Date(child.birthDate).toISOString().split("T")[0]
          : "",
        gender: child.gender || "",
        diagnosis: child.diagnosis || "",
        medicalNotes: child.medicalNotes || "",
        specialNeeds: child.specialNeeds || "",
        notes: child.notes || "",
      });
    } else {
      setEditingChild(null);
      setChildFormData({
        customerId: customerId,
        name: "",
        birthDate: "",
        gender: "",
        diagnosis: "",
        medicalNotes: "",
        specialNeeds: "",
        notes: "",
      });
    }
    setOpenChildDialog(true);
  };

  const handleCloseChildDialog = () => {
    setOpenChildDialog(false);
    setEditingChild(null);
  };

  const handleChildSubmit = async () => {
    try {
      if (!childFormData.name.trim()) {
        showToast("Çocuk adı gereklidir", "error");
        return;
      }

      if (editingChild) {
        await dispatch(
          updateChild({ id: editingChild._id, data: childFormData })
        ).unwrap();
        showToast("Çocuk bilgileri güncellendi");
      } else {
        await dispatch(createChild(childFormData)).unwrap();
        showToast("Çocuk eklendi");
      }
      handleCloseChildDialog();
      dispatch(fetchChildrenByCustomer(customerId));
    } catch (error) {
      showToast(error || "İşlem başarısız", "error");
    }
  };

  const handleDeleteChildConfirm = async () => {
    try {
      await dispatch(deleteChild(childToDelete)).unwrap();
      showToast("Çocuk silindi", "info");
      dispatch(fetchChildrenByCustomer(customerId));
    } catch (error) {
      showToast("Silme başarısız", "error");
    } finally {
      setConfirmDeleteChild(false);
      setChildToDelete(null);
    }
  };

  const handlePaymentToggle = async (session) => {
    try {
      await dispatch(
        updateSessionPayment({
          id: session._id,
          isPaid: !session.isPaid,
          paymentDate: !session.isPaid ? new Date().toISOString() : null,
          paymentMethod: !session.isPaid ? "cash" : null,
        })
      ).unwrap();
      showToast(
        session.isPaid
          ? "Ödeme iptal edildi"
          : "Ödeme alındı olarak işaretlendi"
      );
      dispatch(fetchSessions({}));
    } catch (error) {
      showToast("Ödeme durumu güncellenemedi", "error");
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      await dispatch(deleteSession(sessionToDelete)).unwrap();
      showToast("Seans silindi", "info");
      dispatch(fetchSessions({}));
    } catch {
      showToast("Silme başarısız", "error");
    } finally {
      setConfirmDelete(false);
      setSessionToDelete(null);
    }
  };

  const handleExportPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFilter.startDate)
        params.append("startDate", dateFilter.startDate);
      if (dateFilter.endDate) params.append("endDate", dateFilter.endDate);

      const response = await fetch(
        `https://api.dilanaslan.com/api/pdf/customer-report/${customerId}?${params.toString()}`
      );

      if (!response.ok) {
        throw new Error("PDF oluşturulamadı");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${customer?.fullName || "musteri"}_seans_raporu_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showToast("PDF başarıyla indirildi");
    } catch (error) {
      console.error("PDF indirme hatası:", error);
      showToast("PDF indirilemedi", "error");
    }
  };

  const handleSendSms = async () => {
    try {
      if (!smsMessage.trim()) {
        showToast("Lütfen bir mesaj yazın", "error");
        return;
      }

      if (!customer || !customer._id) {
        showToast("Müşteri bilgisi bulunamadı", "error");
        return;
      }

      const response = await fetch(
        "https://api.dilanaslan.com/api/messages/single",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            customerId: customer._id,
            message: smsMessage,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        showToast("SMS başarıyla gönderildi", "success");
        setOpenSmsDialog(false);
        setSmsMessage("");
      } else {
        showToast(result.message || "SMS gönderilemedi", "error");
      }
    } catch (error) {
      console.error("SMS gönderme hatası:", error);
      showToast("SMS gönderilirken bir hata oluştu", "error");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "scheduled":
        return "primary";
      case "cancelled":
        return "error";
      case "no_show":
        return "warning";
      default:
        return "default";
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Tamamlandı";
      case "scheduled":
        return "Planlandı";
      case "cancelled":
        return "İptal";
      case "no_show":
        return "Gelmedi";
      default:
        return status;
    }
  };

  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age;
  };

  if (!customer) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography>Müşteri yükleniyor...</Typography>
      </Container>
    );
  }

  const stats = calculateStats();
  const filteredSessions = getFilteredSessions();

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <IconButton onClick={() => navigate("/admin/customers")}>
          <ArrowBack />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="600">
            {customer.fullName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Müşteri Detayları
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Sms />}
          onClick={() => setOpenSmsDialog(true)}
          sx={{ mr: 1 }}
        >
          SMS Gönder
        </Button>
        <Button
          variant="outlined"
          startIcon={<PictureAsPdf />}
          onClick={handleExportPDF}
          color="error"
        >
          PDF İndir
        </Button>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Genel Bilgiler" />
          <Tab label={`Çocuklar (${children.length})`} />
          <Tab label={`Seanslar (${customerSessions.length})`} />
          <Tab label={`Paketler (${customerPackages.length})`} />
        </Tabs>
      </Paper>

      {/* Tab 0: Genel Bilgiler */}
      {currentTab === 0 && (
        <Grid container spacing={3}>
          {/* Müşteri Kartı */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: "100%" }}>
              <CardContent sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "primary.main",
                      fontSize: 32,
                      mr: 2,
                    }}
                  >
                    {customer.fullName.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="h5" fontWeight="600">
                      {customer.fullName}
                    </Typography>
                    <Chip
                      label={
                        customer.status === "active"
                          ? "Aktif Müşteri"
                          : "Pasif Müşteri"
                      }
                      color={
                        customer.status === "active" ? "success" : "default"
                      }
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Phone color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Telefon
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {customer.phone}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Email color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        E-posta
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {customer.email || "Belirtilmemiş"}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <CalendarMonth color="action" fontSize="small" />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Kayıt Tarihi
                      </Typography>
                      <Typography variant="body1" fontWeight="500">
                        {new Date(customer.createdAt).toLocaleDateString(
                          "tr-TR",
                          {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                          }
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {customer.address && (
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <Home color="action" fontSize="small" sx={{ mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Adres
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {customer.address}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {customer.notes && (
                    <Box
                      sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}
                    >
                      <Notes color="action" fontSize="small" sx={{ mt: 0.5 }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Notlar
                        </Typography>
                        <Typography variant="body1" fontWeight="500">
                          {customer.notes}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* İstatistikler */}
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card sx={{ bgcolor: "primary.main", color: "white" }}>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <EventNote />
                      <Typography variant="h6">Toplam Seans</Typography>
                    </Box>
                    <Typography variant="h3" fontWeight="700">
                      {stats.totalSessions}
                    </Typography>
                    <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                      <Chip
                        icon={<CheckCircle />}
                        label={`${stats.paidSessions} Ödendi`}
                        size="small"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "white",
                        }}
                      />
                      <Chip
                        icon={<Cancel />}
                        label={`${stats.unpaidSessions} Ödenmedi`}
                        size="small"
                        sx={{
                          bgcolor: "rgba(255,255,255,0.2)",
                          color: "white",
                        }}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  sx={{
                    bgcolor: "success.main",
                    color: "white",
                    height: "100%",
                  }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Ödenen Gelir
                    </Typography>
                    <Typography variant="h4" fontWeight="700">
                      {stats.totalPaid.toFixed(2)} ₺
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      {stats.paidSessions} seans
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Card
                  sx={{ bgcolor: "error.main", color: "white", height: "100%" }}
                >
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      Bekleyen Ödeme
                    </Typography>
                    <Typography variant="h4" fontWeight="700">
                      {stats.totalUnpaid.toFixed(2)} ₺
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                      {stats.unpaidSessions} seans
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12}>
                <Card>
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <AttachMoney color="primary" />
                      <Typography variant="h6">Toplam Gelir (Tümü)</Typography>
                    </Box>
                    <Typography variant="h4" fontWeight="700" color="primary">
                      {stats.totalRevenue.toFixed(2)} ₺
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}

      {/* Tab 1: Çocuklar */}
      {currentTab === 1 && (
        <Box>
          {children.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: "center" }}>
              <ChildCare
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                Bu müşteriye ait çocuk kaydı bulunmamaktadır
              </Typography>
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {children.map((child, index) => (
                <Grid item xs={12} md={6} key={child._id}>
                  <Card
                    sx={{
                      height: "100%",
                      position: "relative",
                      overflow: "visible",
                    }}
                  >
                    <Box
                      sx={{
                        position: "absolute",
                        top: -10,
                        right: 20,
                        bgcolor: "primary.main",
                        color: "white",
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        fontSize: 18,
                        boxShadow: 3,
                      }}
                    >
                      {index + 1}
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleOpenChildDialog(child)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setChildToDelete(child._id);
                            setConfirmDeleteChild(true);
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>

                      {/* Çocuk Başlığı */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="h5" fontWeight="600" gutterBottom>
                          {child.name}
                        </Typography>
                        {child.birthDate && (
                          <Typography variant="body2" color="text.secondary">
                            {calculateAge(child.birthDate)} yaşında
                          </Typography>
                        )}
                      </Box>

                      <Divider sx={{ mb: 2 }} />

                      {/* Temel Bilgiler */}

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        {child.birthDate && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Cake color="action" fontSize="small" />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Doğum Tarihi
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {new Date(child.birthDate).toLocaleDateString(
                                  "tr-TR",
                                  {
                                    year: "numeric",
                                    month: "long",
                                    day: "numeric",
                                  }
                                )}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {child.gender && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 2,
                            }}
                          >
                            <Wc color="action" fontSize="small" />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Cinsiyet
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {child.gender === "male"
                                  ? "Erkek"
                                  : child.gender === "female"
                                  ? "Kız"
                                  : "Diğer"}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {child.diagnosis && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Psychology
                              color="action"
                              fontSize="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Tanı
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {child.diagnosis}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {child.medicalNotes && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <MedicalServices
                              color="action"
                              fontSize="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Tıbbi Notlar
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {child.medicalNotes}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {child.specialNeeds && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Accessible
                              color="action"
                              fontSize="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Özel İhtiyaçlar
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {child.specialNeeds}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {child.notes && (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 2,
                            }}
                          >
                            <Notes
                              color="action"
                              fontSize="small"
                              sx={{ mt: 0.5 }}
                            />
                            <Box sx={{ flex: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Genel Notlar
                              </Typography>
                              <Typography variant="body1" fontWeight="500">
                                {child.notes}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>

                      {/* Kayıt Tarihi */}
                      <Box
                        sx={{
                          mt: 3,
                          pt: 2,
                          borderTop: 1,
                          borderColor: "divider",
                        }}
                      >
                        <Typography variant="caption" color="text.secondary">
                          Kayıt Tarihi:{" "}
                          {new Date(child.createdAt).toLocaleDateString(
                            "tr-TR"
                          )}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* Tab 2: Seanslar */}
      {currentTab === 2 && (
        <Paper sx={{ p: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Typography variant="h6" fontWeight="600">
              Seanslar
            </Typography>
          </Box>

          {/* Tarih Filtreleri */}
          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Başlangıç Tarihi"
                  InputLabelProps={{ shrink: true }}
                  value={dateFilter.startDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, startDate: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Bitiş Tarihi"
                  InputLabelProps={{ shrink: true }}
                  value={dateFilter.endDate}
                  onChange={(e) =>
                    setDateFilter({ ...dateFilter, endDate: e.target.value })
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setDateFilter({ startDate: "", endDate: "" })}
                  sx={{ height: "40px" }}
                >
                  Filtreyi Temizle
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ overflowX: "auto" }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: "grey.100" }}>
                  <TableCell sx={{ fontWeight: 600 }}>Tarih</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Çocuk</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Seans Tipi</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Süre</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Fiyat</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Ödeme</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Durum</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    İşlemler
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredSessions.map((session) => (
                  <TableRow key={session._id} hover>
                    <TableCell>
                      {new Date(session.sessionDate).toLocaleDateString(
                        "tr-TR"
                      )}
                    </TableCell>
                    <TableCell>{session.childId?.name}</TableCell>
                    <TableCell>{session.sessionType}</TableCell>
                    <TableCell>{session.duration} dk</TableCell>
                    <TableCell>
                      <Typography fontWeight="500">
                        {session.price} ₺
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={session.isPaid ? <Check /> : <Close />}
                        label={session.isPaid ? "Ödendi" : "Ödenmedi"}
                        color={session.isPaid ? "success" : "error"}
                        size="small"
                        onClick={() => handlePaymentToggle(session)}
                        sx={{ cursor: "pointer" }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(session.status)}
                        color={getStatusColor(session.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(session)}
                        sx={{ mr: 1 }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setSessionToDelete(session._id);
                          setConfirmDelete(true);
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {filteredSessions.length === 0 && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <EventNote
                sx={{ fontSize: 64, color: "text.secondary", mb: 2 }}
              />
              <Typography variant="h6" color="text.secondary">
                Seçili tarih aralığında seans bulunamadı
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {/* Tab 3: Paketler */}
      {currentTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
            <Typography variant="h6" fontWeight="600">Paketler</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={() => {
              setPackageFormData({
                packageId: "",
                purchaseDate: new Date().toISOString().split("T")[0],
                expiryDate: "",
                isPaid: false,
                paymentDate: "",
                paymentMethod: "",
                notes: "",
              });
              setOpenPackageDialog(true);
            }}>
              Paket Sat
            </Button>
          </Box>

          {customerPackages.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography variant="h6" color="text.secondary">Bu müşteriye henüz paket atanmadı</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {customerPackages.map((cp) => {
                const progressPct = cp.totalSessions > 0
                  ? Math.round((cp.usedSessions / cp.totalSessions) * 100)
                  : 0;
                const statusColor = cp.status === "active" ? "success" : cp.status === "completed" ? "default" : "error";
                const statusText = { active: "Aktif", completed: "Tamamlandı", expired: "Süresi Doldu", cancelled: "İptal" }[cp.status] || cp.status;

                return (
                  <Grid item xs={12} sm={6} md={4} key={cp._id}>
                    <Card sx={{ border: `2px solid ${cp.packageId?.color || "#2563EB"}22`, height: "100%" }}>
                      <CardContent>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: cp.packageId?.color || "#2563EB" }} />
                            <Typography variant="subtitle1" fontWeight="700">{cp.packageName}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 0.5 }}>
                            <IconButton size="small" color="error" onClick={() => { setPackageToDelete(cp._id); setConfirmDeletePackage(true); }}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>

                        <Chip label={statusText} color={statusColor} size="small" sx={{ mb: 2 }} />

                        <Box sx={{ mb: 2 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                            <Typography variant="body2" color="text.secondary">Seans Kullanımı</Typography>
                            <Typography variant="body2" fontWeight="600">
                              {cp.usedSessions} / {cp.totalSessions}
                            </Typography>
                          </Box>
                          <Box sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1, height: 8 }}>
                            <Box sx={{
                              width: `${progressPct}%`,
                              bgcolor: cp.packageId?.color || "#2563EB",
                              borderRadius: 1,
                              height: 8,
                              transition: "width 0.3s",
                            }} />
                          </Box>
                          <Typography variant="caption" color="text.secondary">{cp.remainingSessions} seans kaldı</Typography>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Fiyat</Typography>
                            <Typography variant="caption" fontWeight="600">{cp.packagePrice?.toLocaleString("tr-TR")} TL</Typography>
                          </Box>
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Satın Alma</Typography>
                            <Typography variant="caption">{new Date(cp.purchaseDate).toLocaleDateString("tr-TR")}</Typography>
                          </Box>
                          {cp.expiryDate && (
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                              <Typography variant="caption" color="text.secondary">Son Kullanım</Typography>
                              <Typography variant="caption">{new Date(cp.expiryDate).toLocaleDateString("tr-TR")}</Typography>
                            </Box>
                          )}
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">Ödeme</Typography>
                            <Chip
                              icon={cp.isPaid ? <Check /> : <Close />}
                              label={cp.isPaid ? "Ödendi" : "Ödenmedi"}
                              color={cp.isPaid ? "success" : "error"}
                              size="small"
                              onClick={() => {
                                dispatch(updateCustomerPackagePayment({
                                  id: cp._id,
                                  isPaid: !cp.isPaid,
                                  paymentDate: !cp.isPaid ? new Date().toISOString() : null,
                                  paymentMethod: !cp.isPaid ? "cash" : null,
                                })).then(() => dispatch(fetchCustomerPackages(customerId)));
                              }}
                              sx={{ cursor: "pointer" }}
                            />
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Paket Satış Dialog */}
      <Dialog
        open={openPackageDialog}
        onClose={() => setOpenPackageDialog(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {/* Renkli başlık bandı */}
        <Box sx={{
          background: "linear-gradient(135deg, #1e3a5f 0%, #2563EB 100%)",
          px: 3, py: 2.5,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <Box>
            <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700, mb: 0.3 }}>
              Müşteriye Paket Sat
            </Typography>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.65)" }}>
              {customer?.fullName}
            </Typography>
          </Box>
          <IconButton onClick={() => setOpenPackageDialog(false)} sx={{ color: "rgba(255,255,255,0.7)" }}>
            <Cancel />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 3 }}>
          {/* Paket seçimi — kart listesi */}
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: "text.secondary" }}>
            Paket Seçin *
          </Typography>
          {packages.length === 0 ? (
            <Box sx={{ p: 3, textAlign: "center", bgcolor: "grey.50", borderRadius: 2 }}>
              <Typography color="text.secondary" fontSize={14}>
                Tanımlı aktif paket yok. Önce "Paket Yönetimi"nden paket oluşturun.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 3 }}>
              {packages.map((pkg) => {
                const selected = packageFormData.packageId === pkg._id;
                return (
                  <Box
                    key={pkg._id}
                    onClick={() => setPackageFormData({ ...packageFormData, packageId: pkg._id })}
                    sx={{
                      border: selected
                        ? `2px solid ${pkg.color || "#2563EB"}`
                        : "2px solid transparent",
                      outline: selected ? "none" : "1px solid #e5e7eb",
                      borderRadius: 2.5,
                      p: 2,
                      cursor: "pointer",
                      background: selected ? `${pkg.color || "#2563EB"}08` : "#fff",
                      transition: "all 0.15s",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      "&:hover": {
                        borderColor: pkg.color || "#2563EB",
                        background: `${pkg.color || "#2563EB"}05`,
                      },
                    }}
                  >
                    {/* Renk nokta */}
                    <Box sx={{
                      width: 44, height: 44, borderRadius: 1.5, flexShrink: 0,
                      bgcolor: `${pkg.color || "#2563EB"}18`,
                      display: "flex", alignItems: "center", justifyContent: "center",
                    }}>
                      <Typography fontSize={20}>📦</Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700} fontSize={15}>{pkg.name}</Typography>
                      <Typography fontSize={13} color="text.secondary">
                        {pkg.sessionCount} seans
                      </Typography>
                    </Box>

                    <Box sx={{ textAlign: "right" }}>
                      <Typography fontWeight={700} fontSize={16} color={pkg.color || "#2563EB"}>
                        {pkg.price?.toLocaleString("tr-TR")} TL
                      </Typography>
                      <Typography fontSize={11} color="text.secondary">
                        {Math.round(pkg.price / pkg.sessionCount).toLocaleString("tr-TR")} TL/seans
                      </Typography>
                    </Box>

                    {selected && (
                      <CheckCircle sx={{ color: pkg.color || "#2563EB", fontSize: 22, flexShrink: 0 }} />
                    )}
                  </Box>
                );
              })}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Tarihler */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Satın Alma Tarihi" type="date"
                InputLabelProps={{ shrink: true }}
                value={packageFormData.purchaseDate}
                onChange={(e) => setPackageFormData({ ...packageFormData, purchaseDate: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" label="Son Kullanım (opsiyonel)" type="date"
                InputLabelProps={{ shrink: true }}
                value={packageFormData.expiryDate}
                onChange={(e) => setPackageFormData({ ...packageFormData, expiryDate: e.target.value })}
              />
            </Grid>
          </Grid>

          {/* Ödeme durumu toggle */}
          <Box sx={{
            border: "1px solid #e5e7eb",
            borderRadius: 2,
            overflow: "hidden",
            mb: 2,
          }}>
            <Box sx={{
              px: 2, py: 1.5, bgcolor: "grey.50",
              borderBottom: "1px solid #e5e7eb",
            }}>
              <Typography fontWeight={600} fontSize={13} color="text.secondary">
                Ödeme Durumu
              </Typography>
            </Box>
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: "flex", gap: 1.5, mb: packageFormData.isPaid ? 2 : 0 }}>
                {[
                  { value: false, label: "Ödenmedi", icon: "✕", color: "#ef4444" },
                  { value: true, label: "Ödendi", icon: "✓", color: "#22c55e" },
                ].map((opt) => (
                  <Box
                    key={String(opt.value)}
                    onClick={() => setPackageFormData({ ...packageFormData, isPaid: opt.value })}
                    sx={{
                      flex: 1, py: 1.5, borderRadius: 2, cursor: "pointer",
                      textAlign: "center", fontWeight: 600, fontSize: 14,
                      border: packageFormData.isPaid === opt.value
                        ? `2px solid ${opt.color}`
                        : "2px solid #e5e7eb",
                      color: packageFormData.isPaid === opt.value ? opt.color : "text.secondary",
                      bgcolor: packageFormData.isPaid === opt.value ? `${opt.color}10` : "#fff",
                      transition: "all 0.15s",
                      "&:hover": { borderColor: opt.color },
                    }}
                  >
                    {opt.icon} {opt.label}
                  </Box>
                ))}
              </Box>

              {packageFormData.isPaid && (
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Ödeme Tarihi" type="date"
                      InputLabelProps={{ shrink: true }}
                      value={packageFormData.paymentDate}
                      onChange={(e) => setPackageFormData({ ...packageFormData, paymentDate: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Ödeme Yöntemi</InputLabel>
                      <Select
                        value={packageFormData.paymentMethod}
                        label="Ödeme Yöntemi"
                        onChange={(e) => setPackageFormData({ ...packageFormData, paymentMethod: e.target.value })}
                      >
                        <MenuItem value="cash">💵 Nakit</MenuItem>
                        <MenuItem value="credit_card">💳 Kredi Kartı</MenuItem>
                        <MenuItem value="bank_transfer">🏦 Havale</MenuItem>
                        <MenuItem value="other">Diğer</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Box>

          {/* Notlar */}
          <TextField
            fullWidth size="small" label="Notlar (opsiyonel)" multiline rows={2}
            value={packageFormData.notes}
            onChange={(e) => setPackageFormData({ ...packageFormData, notes: e.target.value })}
            placeholder="Paket hakkında not ekleyin..."
          />
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #f0f0f0", gap: 1 }}>
          <Button
            onClick={() => setOpenPackageDialog(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            disabled={!packageFormData.packageId}
            sx={{ borderRadius: 2, px: 4, fontWeight: 700 }}
            onClick={async () => {
              try {
                await dispatch(createCustomerPackage({
                  customerId,
                  ...packageFormData,
                })).unwrap();
                showToast("Paket atandı");
                setOpenPackageDialog(false);
                dispatch(fetchCustomerPackages(customerId));
              } catch (err) {
                showToast(err || "Paket atanamadı", "error");
              }
            }}
          >
            Paketi Sat
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paket Silme Onayı */}
      <Dialog open={confirmDeletePackage} onClose={() => setConfirmDeletePackage(false)}>
        <DialogTitle>Paketi Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu müşteri paketini silmek istediğinizden emin misiniz?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeletePackage(false)}>İptal</Button>
          <Button color="error" variant="contained" onClick={async () => {
            try {
              await dispatch(deleteCustomerPackage(packageToDelete)).unwrap();
              showToast("Paket silindi", "info");
              dispatch(fetchCustomerPackages(customerId));
            } catch { showToast("Silme başarısız", "error"); }
            finally { setConfirmDeletePackage(false); setPackageToDelete(null); }
          }}>Sil</Button>
        </DialogActions>
      </Dialog>

      {/* Seans Ekleme/Düzenleme Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingSession ? "Seans Düzenle" : "Yeni Seans"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Çocuk *</InputLabel>
                <Select
                  value={formData.childId}
                  label="Çocuk *"
                  onChange={(e) =>
                    setFormData({ ...formData, childId: e.target.value })
                  }
                >
                  {children.map((child) => (
                    <MenuItem key={child._id} value={child._id}>
                      {child.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tarih *"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.sessionDate}
                onChange={(e) =>
                  setFormData({ ...formData, sessionDate: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Seans Tipi *"
                value={formData.sessionType}
                onChange={(e) =>
                  setFormData({ ...formData, sessionType: e.target.value })
                }
                placeholder="Örn: Oyun Terapisi"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Durum</InputLabel>
                <Select
                  value={formData.status}
                  label="Durum"
                  onChange={(e) =>
                    setFormData({ ...formData, status: e.target.value })
                  }
                >
                  <MenuItem value="scheduled">Planlandı</MenuItem>
                  <MenuItem value="completed">Tamamlandı</MenuItem>
                  <MenuItem value="cancelled">İptal</MenuItem>
                  <MenuItem value="no_show">Gelmedi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Süre (dakika)"
                type="number"
                value={formData.duration}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    duration: parseInt(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Fiyat (TL) *"
                type="number"
                value={formData.price}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    price: parseFloat(e.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Ödeme Durumu</InputLabel>
                <Select
                  value={formData.isPaid}
                  label="Ödeme Durumu"
                  onChange={(e) =>
                    setFormData({ ...formData, isPaid: e.target.value })
                  }
                >
                  <MenuItem value={false}>Ödenmedi</MenuItem>
                  <MenuItem value={true}>Ödendi</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {formData.isPaid && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Ödeme Tarihi"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={formData.paymentDate}
                    onChange={(e) =>
                      setFormData({ ...formData, paymentDate: e.target.value })
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Ödeme Yöntemi</InputLabel>
                    <Select
                      value={formData.paymentMethod}
                      label="Ödeme Yöntemi"
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          paymentMethod: e.target.value,
                        })
                      }
                    >
                      <MenuItem value="cash">Nakit</MenuItem>
                      <MenuItem value="credit_card">Kredi Kartı</MenuItem>
                      <MenuItem value="bank_transfer">Havale</MenuItem>
                      <MenuItem value="other">Diğer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notlar"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) =>
                  setFormData({ ...formData, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.childId ||
              !formData.sessionDate ||
              !formData.sessionType ||
              !formData.price
            }
          >
            {editingSession ? "Güncelle" : "Oluştur"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SMS Dialog */}
      <Dialog
        open={openSmsDialog}
        onClose={() => setOpenSmsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>SMS Gönder</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Alıcı: {customer.fullName} ({customer.phone})
          </Typography>
          <TextField
            fullWidth
            label="Mesajınız"
            multiline
            rows={5}
            value={smsMessage}
            onChange={(e) => setSmsMessage(e.target.value)}
            placeholder="SMS mesajınızı yazın..."
            helperText={`${smsMessage.length} karakter`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSmsDialog(false)}>İptal</Button>
          <Button
            onClick={handleSendSms}
            variant="contained"
            disabled={!smsMessage.trim()}
            startIcon={<Sms />}
          >
            Gönder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Silme Onayı Dialog */}
      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Seansı Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu seansı silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>İptal</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openChildDialog}
        onClose={handleCloseChildDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingChild ? "Çocuk Bilgilerini Düzenle" : "Yeni Çocuk Ekle"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Çocuk Adı *"
                value={childFormData.name}
                onChange={(e) =>
                  setChildFormData({ ...childFormData, name: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Doğum Tarihi"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={childFormData.birthDate}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    birthDate: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Cinsiyet</InputLabel>
                <Select
                  value={childFormData.gender}
                  label="Cinsiyet"
                  onChange={(e) =>
                    setChildFormData({
                      ...childFormData,
                      gender: e.target.value,
                    })
                  }
                >
                  <MenuItem value="">Seçiniz</MenuItem>
                  <MenuItem value="male">Erkek</MenuItem>
                  <MenuItem value="female">Kız</MenuItem>
                  <MenuItem value="other">Diğer</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Tanı"
                value={childFormData.diagnosis}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    diagnosis: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tıbbi Notlar"
                multiline
                rows={2}
                value={childFormData.medicalNotes}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    medicalNotes: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Özel İhtiyaçlar"
                multiline
                rows={2}
                value={childFormData.specialNeeds}
                onChange={(e) =>
                  setChildFormData({
                    ...childFormData,
                    specialNeeds: e.target.value,
                  })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Genel Notlar"
                multiline
                rows={3}
                value={childFormData.notes}
                onChange={(e) =>
                  setChildFormData({ ...childFormData, notes: e.target.value })
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChildDialog}>İptal</Button>
          <Button
            onClick={handleChildSubmit}
            variant="contained"
            disabled={!childFormData.name.trim()}
          >
            {editingChild ? "Güncelle" : "Ekle"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Çocuk Silme Onayı Dialog */}
      <Dialog
        open={confirmDeleteChild}
        onClose={() => setConfirmDeleteChild(false)}
      >
        <DialogTitle>Çocuğu Sil</DialogTitle>
        <DialogContent>
          <Typography>
            Bu çocuğu silmek istediğinizden emin misiniz? Bu işlem geri
            alınamaz.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteChild(false)}>İptal</Button>
          <Button
            onClick={handleDeleteChildConfirm}
            color="error"
            variant="contained"
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.show}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, show: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.type}>{toast.message}</Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminCustomerDetailPage;
